import { useState } from "react";
import { ActionList, ActionMenu, IconButton, Text } from "@primer/react";
import { ChevronRightIcon, PencilIcon, ShieldCheckIcon, TrashIcon } from "@primer/octicons-react";
import { Alert } from "../components/Alert/Alert.jsx";
import { AssetIdentity, assetHref } from "../components/AssetIdentity/AssetIdentity.jsx";
import { Button } from "../components/Button/Button.jsx";
import { ComplianceResultGroups, complianceResultTone } from "../components/ComplianceResultGroups/ComplianceResultGroups.jsx";
import { ComplianceResultItem } from "../components/ComplianceResultItem/ComplianceResultItem.jsx";
import { EditablePercentage, formatPercentage } from "../components/EditablePercentage/EditablePercentage.jsx";
import { FormBody, FormContainer, FormFooter } from "../components/FormContainer/FormContainer.jsx";
import { PageHeading } from "../components/PageHeading/PageHeading.jsx";
import { PolicyFinding } from "../components/PolicyFinding/PolicyFinding.jsx";
import { policyDisplayTitle, policyHref, policyTitle } from "../components/PolicyIdentity/PolicyIdentity.jsx";
import { PolicyStatus } from "../components/PolicyStatus/PolicyStatus.jsx";
import { PolicyRangeMeter } from "../components/PolicyRangeMeter/PolicyRangeMeter.jsx";
import { amountForBasisPoints, formatPortfolioAmount, usePortfolioValue } from "../components/PortfolioValue/PortfolioValue.jsx";
import { PropertyValueIdentity, propertyValueHref } from "../components/PropertyValueIdentity/PropertyValueIdentity.jsx";
import { Section, SectionStack } from "../components/Section/Section.jsx";
import { evaluateCompliance } from "../domain/compliance.js";
import { createPolicy, deletePolicy, updatePolicy, validatePolicyDraft } from "../domain/document.js";
import { navigateToHash } from "../lib/navigateToHash.js";
import { useApplicationDocument } from "../storage/useApplicationDocument.js";
import "./PolicyPage.css";

export function PolicyPage({ policyId, repository }) {
  const application = useApplicationDocument(repository);
  if (application.error && !application.document) return <Alert tone="danger" heading="Unable to open Regulator">{application.error.message}</Alert>;
  if (!application.document) return <PolicyLoading />;
  return <PolicyContent application={application} policyId={policyId} />;
}

function PolicyContent({ application, policyId }) {
  const { document } = application;
  const isNew = policyId === "new";
  const policy = isNew ? null : (document.policies ?? []).find((item) => item.id === policyId);
  const [editing, setEditing] = useState(isNew);
  const [draft, setDraft] = useState(() => isNew ? newPolicyDraft(document) : null);
  const [saveError, setSaveError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!isNew && !policy) return <MissingPolicy policyId={policyId} />;

  function startEditing() {
    setDraft(structuredClone(policy));
    setSaveError(null);
    setConfirmDelete(false);
    setEditing(true);
  }

  function cancelEditing() {
    if (isNew) {
      navigateToHash("#/policies");
      return;
    }
    setEditing(false);
    setDraft(null);
    setSaveError(null);
    setConfirmDelete(false);
  }

  async function savePolicy() {
    setSaveError(null);
    try {
      const nextDocument = isNew ? createPolicy(document, draft) : updatePolicy(document, policy.id, draft);
      if (await application.save(nextDocument)) {
        const savedPolicy = isNew ? nextDocument.policies.at(-1) : nextDocument.policies.find((item) => item.id === policy.id);
        if (isNew) navigateToHash(policyHref(savedPolicy.id));
        else {
          setEditing(false);
          setDraft(null);
        }
      }
    } catch (error) {
      setSaveError(error.message);
    }
  }

  async function removePolicy() {
    if (await application.save(deletePolicy(document, policy.id))) navigateToHash("#/policies");
  }

  async function togglePolicy() {
    await application.save(updatePolicy(document, policy.id, { ...policy, enabled: policy.enabled === false }));
  }

  const title = isNew ? { label: "New policy", primary: "New policy" } : policyDisplayTitle(document, policy);
  const evaluation = policy ? evaluateCompliance(document).find((item) => item.policy.id === policy.id) : null;

  return <article className="policy-page">
    <PageHeading
      description={policy ? <PolicyStatus document={document} policy={policy} /> : "Define the required and preferred allocation range."}
      titleLabel={title.label}
      actions={!editing ? <><Button tone="secondary" onClick={togglePolicy} disabled={application.saving}>{policy.enabled === false ? "Enable policy" : "Disable policy"}</Button><Button tone="secondary" leadingVisual={PencilIcon} onClick={startEditing} labelDetail="policy">Edit</Button></> : null}
    ><PolicyPageTitle title={title} /></PageHeading>
    {application.error ? <Alert tone="danger" heading="Changes were not saved">{application.error.message}</Alert> : null}
    {policy?.enabled === false ? <Alert heading="Policy disabled">This policy is disabled and is not monitored for this profile.</Alert> : null}
    {editing ? <PolicyEditor document={document} draft={draft} existingPolicy={policy} onCancel={cancelEditing} onChange={setDraft} onDelete={policy ? () => setConfirmDelete(true) : null} onSave={savePolicy} saveError={saveError} saving={application.saving} /> : <SectionStack>
      <Section heading="Definition" description="Required limits and preferred range.">
        <PolicyDefinition policy={policy} />
      </Section>
      {policy?.enabled !== false ? <Section heading="Current compliance" description="Results from the current portfolio allocation.">
        <PolicyResults document={document} evaluation={evaluation} />
      </Section> : null}
    </SectionStack>}
    {confirmDelete ? <DeletePolicyConfirmation document={document} onCancel={() => setConfirmDelete(false)} onConfirm={removePolicy} policy={policy} saving={application.saving} /> : null}
  </article>;
}

function newPolicyDraft(document) {
  const property = document.properties[0];
  const scope = property
    ? { type: "specific", propertyId: property.id, valueId: property.values[0].id }
    : { type: "concentration", dimension: { type: "asset" } };
  return { scope, must: { maximum: 1000 }, should: {}, enabled: true };
}

function PolicyDefinition({ policy }) {
  return <div className="policy-definition">
    <div className="policy-clauses">{policyClauses(policy).map((clause, index) => <Clause clause={clause} editable={false} first={index === 0} key={clause.key} policy={policy} />)}</div>
    <PolicyRangeMeter policy={policy} />
  </div>;
}

function PolicyEditor({ document, draft, existingPolicy, onCancel, onChange, onDelete, onSave, saveError, saving }) {
  const validationError = validatePolicyDraft(document, draft, existingPolicy?.id);
  const hasChanges = !existingPolicy || JSON.stringify(draft) !== JSON.stringify(existingPolicy);
  const clauses = policyClauses(draft);
  return <FormContainer className="policy-editor">
    <FormBody>
      <div className="policy-subject"><ShieldCheckIcon className="policy-icon" aria-hidden="true" /><TypeMenu document={document} policy={draft} onChange={onChange} /><PolicySeparator /><DimensionMenu document={document} policy={draft} onChange={onChange} />{draft.scope.type === "specific" ? <><PolicySeparator /><ValueMenu document={document} policy={draft} onChange={onChange} /></> : null}</div>
      <div className="policy-definition editor">
        <div className="policy-clauses">{clauses.map((clause, index) => <Clause clause={clause} editable first={index === 0} key={clause.key} onChange={onChange} policy={draft} saving={saving} />)}<AddClause policy={draft} onChange={onChange} /></div>
        <PolicyRangeMeter policy={draft} />
      </div>
    </FormBody>
    <FormFooter className="policy-editor-footer" hasChanges={hasChanges}>
      <div><Button onClick={onSave} disabled={!hasChanges || Boolean(validationError) || saving} labelDetail={saving ? undefined : "policy"}>{saving ? "Saving…" : "Save"}</Button><Button tone="quiet" onClick={onCancel}>Cancel</Button></div>
      <Text className={`policy-editor-message${saveError ? " error" : ""}`} role={saveError ? "alert" : "status"}>{saveError ?? validationError}</Text>
      {onDelete ? <Button className="form-danger-action" tone="danger" leadingVisual={TrashIcon} onClick={onDelete} labelDetail="policy">Delete</Button> : null}
    </FormFooter>
  </FormContainer>;
}

function PolicyResults({ document, evaluation }) {
  const findings = (evaluation?.results ?? []).map((result) => ({ key: result.key, result, tone: complianceResultTone(result.status) }));
  return <ComplianceResultGroups
    defaultExpanded={[]}
    empty="There is nothing to evaluate for this policy."
    expandedEdge
    findings={findings}
    renderFinding={({ result }) => <PolicyResult document={document} key={result.key} policy={evaluation.policy} result={result} />}
  />;
}

function PolicyResult({ document, policy, result }) {
  const { value } = usePortfolioValue();
  const href = result.subject.type === "asset"
    ? assetHref(result.subject.asset.id)
    : propertyValueHref(result.subject.propertyId, result.subject.valueId);
  const summary = <><FindingSubject document={document} subject={result.subject} /><Text className="policy-result-allocation">({formatPercentage(result.basisPoints)}%{value !== null ? <span className="portfolio-amount"> · {formatPortfolioAmount(amountForBasisPoints(value, result.basisPoints))}</span> : null})</Text></>;
  const detail = <PolicyFinding document={document} policy={policy} result={result} showIdentity={false} />;
  return <ComplianceResultItem className="policy-result" detail={detail} href={href} status={result.status} summary={summary} />;
}

function FindingSubject({ document, subject }) {
  if (subject.type === "asset") return <AssetIdentity asset={subject.asset} copyableTicker={false} variant="sentence" />;
  const property = document.properties.find((item) => item.id === subject.propertyId);
  const value = property.values.find((item) => item.id === subject.valueId);
  return <PropertyValueIdentity property={property} value={value} />;
}

function PolicyPageTitle({ title }) {
  return <>{title.primary}{title.context ? <Text as="span" className={`policy-title-context ${title.contextTone}`}> {title.context}</Text> : null}</>;
}

function PolicySeparator() { return <ChevronRightIcon className="policy-separator" size="small" aria-hidden="true" />; }

function TypeMenu({ document, onChange, policy }) {
  const selectExposure = () => { const property = document.properties[0]; onChange({ ...policy, scope: { type: "specific", propertyId: property.id, valueId: property.values[0].id } }); };
  return <ActionMenu><ActionMenu.Button className="policy-token menu-token">{policy.scope.type === "specific" ? "Exposure" : "Concentration"}</ActionMenu.Button><ActionMenu.Overlay><ActionList>{document.properties.length ? <ActionList.Item onSelect={selectExposure}>Exposure</ActionList.Item> : null}<ActionList.Item onSelect={() => onChange({ ...policy, scope: { type: "concentration", dimension: { type: "asset" } } })}>Concentration</ActionList.Item></ActionList></ActionMenu.Overlay></ActionMenu>;
}

function DimensionMenu({ document, onChange, policy }) {
  if (policy.scope.type === "specific") {
    const property = document.properties.find((item) => item.id === policy.scope.propertyId);
    return <ActionMenu><ActionMenu.Button className="policy-token menu-token">{property.name}</ActionMenu.Button><ActionMenu.Overlay><ActionList>{document.properties.map((item) => <ActionList.Item key={item.id} onSelect={() => onChange({ ...policy, scope: { type: "specific", propertyId: item.id, valueId: item.values[0].id } })}>{item.name}</ActionList.Item>)}</ActionList></ActionMenu.Overlay></ActionMenu>;
  }
  const dimension = policy.scope.dimension;
  const label = dimension.type === "asset" ? "Asset" : document.properties.find((item) => item.id === dimension.propertyId).name;
  return <ActionMenu><ActionMenu.Button className="policy-token menu-token">{label}</ActionMenu.Button><ActionMenu.Overlay><ActionList><ActionList.Item onSelect={() => onChange({ ...policy, scope: { type: "concentration", dimension: { type: "asset" } } })}>Asset</ActionList.Item>{document.properties.map((item) => <ActionList.Item key={item.id} onSelect={() => onChange({ ...policy, scope: { type: "concentration", dimension: { type: "property", propertyId: item.id } } })}>{item.name}</ActionList.Item>)}</ActionList></ActionMenu.Overlay></ActionMenu>;
}

function ValueMenu({ document, onChange, policy }) {
  const property = document.properties.find((item) => item.id === policy.scope.propertyId);
  const value = property.values.find((item) => item.id === policy.scope.valueId);
  return <ActionMenu><ActionMenu.Button className="policy-token menu-token">{value.name}</ActionMenu.Button><ActionMenu.Overlay><ActionList>{property.values.map((item) => <ActionList.Item key={item.id} onSelect={() => onChange({ ...policy, scope: { ...policy.scope, valueId: item.id } })}>{item.name}</ActionList.Item>)}</ActionList></ActionMenu.Overlay></ActionMenu>;
}

function Clause({ clause, editable, first, onChange, policy, saving }) {
  function remove() {
    const next = { ...policy, [clause.level]: { ...policy[clause.level] } };
    delete next[clause.level][clause.bound];
    onChange(next);
  }
  const required = clause.level === "must" && clause.bound === "maximum";
  return <div className="policy-clause"><Text>{first ? "" : "And "}<strong>{clause.level.toUpperCase()}</strong> {clause.bound === "minimum" ? "be at least" : "not exceed"}</Text><EditablePercentage editable={editable} value={clause.value} label={`${clause.level} ${clause.bound}`} onCommit={(value) => onChange({ ...policy, [clause.level]: { ...policy[clause.level], [clause.bound]: value } })} />{editable && !required ? <IconButton className="clause-control" icon={TrashIcon} variant="invisible" size="small" aria-label={`Remove ${clause.level} ${clause.bound}`} disabled={saving} onClick={remove} /> : <span className="clause-action-space" />}</div>;
}

function AddClause({ onChange, policy }) {
  const options = [["must", "minimum", "And MUST be at least", 0], ["should", "minimum", "And SHOULD be at least", policy.must.minimum ?? 0], ["should", "maximum", "And SHOULD not exceed", policy.must.maximum ?? 10000]].filter(([level, bound]) => policy[level]?.[bound] === undefined);
  if (!options.length) return null;
  return <ActionMenu><ActionMenu.Button className="add-clause" variant="invisible">And…</ActionMenu.Button><ActionMenu.Overlay><ActionList>{options.map(([level, bound, label, value]) => <ActionList.Item key={`${level}-${bound}`} onSelect={() => onChange({ ...policy, [level]: { ...(policy[level] ?? {}), [bound]: value } })}>{label}</ActionList.Item>)}</ActionList></ActionMenu.Overlay></ActionMenu>;
}

function policyClauses(policy) {
  return [["must", "maximum"], ["must", "minimum"], ["should", "minimum"], ["should", "maximum"]].filter(([level, bound]) => policy[level]?.[bound] !== undefined).map(([level, bound]) => ({ key: `${level}-${bound}`, level, bound, value: policy[level][bound] }));
}

function DeletePolicyConfirmation({ document, onCancel, onConfirm, policy, saving }) {
  return <div className="policy-delete"><Alert tone="warning" heading={`Delete ${policyTitle(document, policy)} policy?`}>Assets and allocations are not affected.</Alert><div><Button tone="quiet" onClick={onCancel}>Cancel</Button><Button tone="danger" onClick={onConfirm} disabled={saving} labelDetail={saving ? undefined : "policy"}>{saving ? "Deleting…" : "Delete"}</Button></div></div>;
}

function MissingPolicy({ policyId }) {
  return <article className="policy-page"><PageHeading description="This policy may have been deleted.">Policy not found</PageHeading><Alert tone="warning" heading={`Policy “${policyId}” was not found`}><a href="#/policies">Return to Policies</a></Alert></article>;
}

function PolicyLoading() {
  return <article className="policy-page"><PageHeading description="Loading policy…">Policy</PageHeading></article>;
}
